import React from 'react';
import { Button, Space, Dropdown, Avatar, Typography, theme, Switch } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  DownOutlined,
  LogoutOutlined,
  SettingOutlined,
  BulbOutlined,
  BulbFilled,
  AppstoreOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { StyledHeader } from '../../styles/common';
import type { MenuProps } from 'antd';

const { Text } = Typography;

interface HeaderProps {
  collapsed: boolean;
  onToggle: () => void;
  isMobile?: boolean;
}

const Header: React.FC<HeaderProps> = ({ collapsed, onToggle, isMobile = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, selectedBranch, logout, branches } = useAuth();
  const { token } = theme.useToken();
  const { isDarkMode, toggleTheme } = useTheme();

  const isControlPanel = location.pathname === '/control-panel';
  
  // Check if sidebar should be shown (delivery, warehouse, cycle-counting, and crm routes)
  const showSidebar = location.pathname.startsWith('/delivery') || 
                      location.pathname.startsWith('/tog') || 
                      location.pathname.startsWith('/shipment-batch') || 
                      location.pathname.startsWith('/inbound') ||
                      location.pathname.startsWith('/loading') ||
                      location.pathname.startsWith('/warehouse') ||
                      location.pathname.startsWith('/cycle-counting') ||
                      location.pathname.startsWith('/crm') ||
                      location.pathname.startsWith('/purchases') ||
                      location.pathname.startsWith('/credit-payments') ||
                      location.pathname.startsWith('/inventory') ||
                      location.pathname.startsWith('/access-control');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const items: MenuProps['items'] = [
    {
      key: 'branch',
      label: (
        <div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Branch
          </Text>
          <br />
          <Text strong>{selectedBranch?.name || 'No Branch Selected'}</Text>
          {selectedBranch?.branch && (
            <>
              <br />
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {selectedBranch.branch}
              </Text>
            </>
          )}
        </div>
      ),
      disabled: true,
    },
    {
      type: 'divider',
    },
    ...(branches.length > 1
      ? [
          {
            key: 'change-branch',
            label: 'Change Branch',
            icon: <SwapOutlined />,
            onClick: () => navigate('/select-branch'),
          },
        ]
      : []),
    {
      key: 'settings',
      label: 'Settings',
      icon: <SettingOutlined />,
      onClick: () => navigate('/settings'),
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  return (
    <StyledHeader style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%', width: '100%', padding: isMobile ? '0 8px' : '0 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          {showSidebar && (
            <Button
              type={isDarkMode ? 'text' : 'default'}
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={onToggle}
              style={{ 
                width: isMobile ? 40 : 64,
                height: isMobile ? 40 : 64,
                minWidth: isMobile ? 40 : 64,
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                flexShrink: 0,
              }}
              aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            />
          )}
          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', marginLeft: showSidebar ? 16 : 0, overflow: 'hidden' }}>
              <Text strong style={{ fontSize: '16px', whiteSpace: 'nowrap' }}>{selectedBranch?.name || 'No Branch Selected'}</Text>
              {selectedBranch?.branch && (
                <>
                  <span style={{ margin: '0 8px', color: token.colorTextSecondary }}>|</span>
                  <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>{selectedBranch.branch}</Text>
                </>
              )}
            </div>
          )}
          {isMobile && (
            <Text strong style={{ fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>
              {selectedBranch?.branch || selectedBranch?.name || 'No Branch'}
            </Text>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 16, flexShrink: 0 }}>
          {!isControlPanel && (
            <Button
              icon={<AppstoreOutlined />}
              onClick={() => navigate('/control-panel')}
              type="default"
              size={isMobile ? 'small' : 'middle'}
            >
              {!isMobile && 'Control Panel'}
            </Button>
          )}
          <Switch
            checked={isDarkMode}
            onChange={toggleTheme}
            checkedChildren={<BulbFilled />}
            unCheckedChildren={<BulbOutlined />}
          />
          <Dropdown menu={{ items }} trigger={['click']}>
            <Space style={{ cursor: 'pointer', padding: isMobile ? '2px 4px' : '4px 8px', borderRadius: 4, transition: 'background-color 0.3s' }}>
              <Avatar icon={<UserOutlined />} size={isMobile ? 'small' : 'default'} />
              {!isMobile && <Text>{user ? user.name : 'User'}</Text>}
              <DownOutlined style={{ fontSize: 12 }} />
            </Space>
          </Dropdown>
        </div>
      </div>
    </StyledHeader>
  );
};

export default Header;
